/* ATLETICA PDP — variant selection, gallery, quantity, AJAX add-to-cart, zoom.
   Scoped per [data-atletica-pdp] section; no dependency on Dawn's product-form. */
class AtleticaPDP {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('#atletica-pdp-form') || root.querySelector('form');
    this.variantInput = root.querySelector('[data-variant-id]');
    // (The wishlist bookmark carries a data-price of its own, for its panel.)
    this.priceEl = root.querySelector('[data-price]:not([data-wish-open])');
    this.addBtn = root.querySelector('[data-add]');
    this.addText = root.querySelector('[data-add-text]');
    this.defaultAdd = (this.addBtn && this.addBtn.getAttribute('data-label')) || 'AGREGAR AL CARRITO';
    this.moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    // Button states and size names, as set in the section.
    this.labels = {
      soldOut: root.getAttribute('data-label-soldout') || 'AGOTADO',
      added: root.getAttribute('data-label-added') || 'AÑADIDO ✓',
      unavailable: root.getAttribute('data-label-unavailable') || 'NO DISPONIBLE'
    };
    this.sizeNames = {};
    (root.getAttribute('data-size-names') || '').split(/\n/).forEach((line) => {
      const i = line.indexOf(':');
      if (i > 0) this.sizeNames[line.slice(0, i).trim().toUpperCase()] = line.slice(i + 1).trim();
    });
    this.variants = this.readVariants();
    this.selected = {};
    // The wishlist bookmark beside the name opens the panel on what's picked here.
    this.wish = root.querySelector('[data-wish-open]');
    this.wishImage = this.wish ? this.wish.getAttribute('data-image') : null;

    // Slides: photos, and the product's videos (<video>, or a YouTube/Vimeo frame).
    this.imgs = Array.from(root.querySelectorAll('.atletica-pdp__img'));
    this.thumbs = Array.from(root.querySelectorAll('[data-thumb]'));
    this.zoomBtn = root.querySelector('[data-zoom]');
    this.active = 0;
    // Videos play like moving photos (muted, looping) unless the section turns
    // that off - or the shopper asks for less motion, when they get controls.
    this.autoVideo = root.getAttribute('data-video-auto') === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.autoVideo) {
      this.imgs.forEach((el) => {
        if (el.tagName !== 'VIDEO') return;
        el.removeAttribute('autoplay');
        el.controls = true;
        el.pause();
      });
    }

    this.initSelected();
    this.wireOptions();
    this.wireQty();
    this.wireGallery();
    this.wireAdd();
    this.wireZoom();
    // A video placed first opens the gallery; the chosen colour's photo comes
    // up once the shopper picks one.
    this.update({ keepMedia: !!this.imgs[0] && this.imgs[0].tagName !== 'IMG' });
    this.syncMedia();
  }

  readVariants() {
    const el = this.root.querySelector('[data-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  initSelected() {
    this.root.querySelectorAll('.atletica-pdp__opt').forEach((opt) => {
      const idx = parseInt(opt.getAttribute('data-opt-index'), 10);
      const active = opt.querySelector('.is-active');
      if (active) this.selected[idx] = active.getAttribute('data-value');
    });
  }

  wireOptions() {
    this.root.querySelectorAll('.atletica-pdp__swatch, .atletica-pdp__size').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.hasAttribute('disabled')) return;
        const idx = parseInt(btn.getAttribute('data-opt'), 10);
        this.selected[idx] = btn.getAttribute('data-value');
        const group = btn.parentElement;
        group.querySelectorAll('.is-active').forEach((b) => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        this.update();
      });
    });
  }

  selectedOptionsArray() {
    return Object.keys(this.selected)
      .map(Number)
      .sort((a, b) => a - b)
      .map((k) => this.selected[k]);
  }

  variantFor(optsArray) {
    return this.variants.find((v) => (v.options || []).every((o, i) => o === optsArray[i]));
  }

  update(opts = {}) {
    // labels
    this.root.querySelectorAll('.atletica-pdp__opt').forEach((opt) => {
      const idx = parseInt(opt.getAttribute('data-opt-index'), 10);
      const label = opt.querySelector('[data-opt-label]');
      const val = this.selected[idx];
      if (label && val) label.textContent = this.displayLabel(opt, val);
    });

    const v = this.variantFor(this.selectedOptionsArray());
    if (this.variantInput && v) this.variantInput.value = v.id;
    if (this.priceEl && v) {
      // On sale: the compare-at price struck through before the price.
      this.priceEl.textContent = '';
      if (v.compare_at_price > v.price) {
        const was = document.createElement('s');
        was.className = 'atletica-pdp__price-was';
        was.textContent = this.money(v.compare_at_price);
        this.priceEl.append(was, ' ');
      }
      const now = document.createElement('span');
      now.className = 'atletica-pdp__price-now';
      now.textContent = this.money(v.price);
      this.priceEl.append(now);
    }
    if (this.addBtn) {
      const ok = v && v.available;
      this.addBtn.disabled = !ok;
      if (this.addText) this.addText.textContent = ok ? this.defaultAdd : this.labels.soldOut;
    }
    if (!opts.keepMedia && v && v.featured_media && v.featured_media.id != null) this.showMediaById(v.featured_media.id);
    if (v) this.paintWish(v);
    this.markAvailability();
  }

  paintWish(v) {
    const w = this.wish;
    if (!w) return;
    w.setAttribute('data-variant', v.id);
    const ci = parseInt(w.getAttribute('data-color-index'), 10);
    if (ci >= 0 && v.options) w.setAttribute('data-color', v.options[ci] || '');
    w.setAttribute('data-price', this.money(v.price));
    if (v.compare_at_price > v.price) w.setAttribute('data-compare', this.money(v.compare_at_price));
    else w.removeAttribute('data-compare');
    const src = v.featured_image && v.featured_image.src;
    const img = src ? src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=400' : this.wishImage;
    if (img) w.setAttribute('data-image', img);
    else w.removeAttribute('data-image');
  }

  markAvailability() {
    this.root.querySelectorAll('.atletica-pdp__opt').forEach((opt) => {
      const idx = parseInt(opt.getAttribute('data-opt-index'), 10);
      opt.querySelectorAll('.atletica-pdp__size').forEach((btn) => {
        const candidate = Object.assign({}, this.selected);
        candidate[idx] = btn.getAttribute('data-value');
        const opts = Object.keys(candidate)
          .map(Number)
          .sort((a, b) => a - b)
          .map((k) => candidate[k]);
        const match = this.variantFor(opts);
        btn.toggleAttribute('disabled', !(match && match.available));
      });
    });
  }

  displayLabel(opt, val) {
    if (opt.querySelector('.atletica-pdp__size')) {
      // The section's size names (S: SMALL ...); unlisted sizes as they are.
      return this.sizeNames[String(val).toUpperCase()] || String(val).toUpperCase();
    }
    return String(val).toUpperCase();
  }

  // The store's own money format, as Liquid's | money prints it ({{amount}},
  // {{amount_no_decimals}}, comma or apostrophe separators).
  money(cents) {
    const value = (Number(cents) || 0) / 100;
    const fmt = (n, decimals, thousands, point) => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(point);
    };
    const token = (this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    let out;
    switch (token) {
      case 'amount_no_decimals': out = fmt(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = fmt(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = fmt(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = fmt(value, 2, "'", '.'); break;
      default: out = fmt(value, 2, ',', '.');
    }
    return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, out).replace(/<[^>]*>/g, '');
  }

  // ---- gallery ----
  wireGallery() {
    this.thumbs.forEach((t) =>
      t.addEventListener('click', () => this.showMedia(parseInt(t.getAttribute('data-thumb'), 10)))
    );
    const prev = this.root.querySelector('[data-prev]');
    const next = this.root.querySelector('[data-next]');
    if (prev) prev.addEventListener('click', () => this.showMedia(this.active - 1));
    if (next) next.addEventListener('click', () => this.showMedia(this.active + 1));
  }

  showMedia(i) {
    if (!this.imgs.length) return;
    const n = this.imgs.length;
    i = ((i % n) + n) % n;
    this.imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
    this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === i));
    this.active = i;
    this.syncMedia();
  }

  // The video on show plays (when set to), the rest stop; an embed only
  // loads while it's on show. Zoom is for photos.
  syncMedia() {
    this.imgs.forEach((el, k) => {
      const on = k === this.active;
      if (el.tagName === 'VIDEO') {
        if (!on) el.pause();
        else if (this.autoVideo) {
          const p = el.play();
          if (p && p.catch) p.catch(() => {});
        }
      } else if (el.tagName === 'IFRAME') {
        if (on && !el.getAttribute('src')) el.setAttribute('src', el.getAttribute('data-src'));
        else if (!on && el.getAttribute('src')) el.removeAttribute('src');
      }
    });
    const current = this.imgs[this.active];
    if (this.zoomBtn) this.zoomBtn.hidden = !!current && current.tagName !== 'IMG';
  }

  showMediaById(id) {
    const img = this.imgs.find((im) => im.getAttribute('data-media-id') == id);
    if (img) this.showMedia(this.imgs.indexOf(img));
  }

  // ---- quantity ----
  wireQty() {
    this.qty = this.root.querySelector('[data-qty]');
    const minus = this.root.querySelector('[data-qty-minus]');
    const plus = this.root.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', () => this.bump(-1));
    if (plus) plus.addEventListener('click', () => this.bump(1));
  }

  bump(d) {
    if (!this.qty) return;
    this.qty.value = Math.max(1, (parseInt(this.qty.value, 10) || 1) + d);
  }

  // ---- add to cart (AJAX) ----
  wireAdd() {
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!this.addBtn || this.addBtn.disabled) return;
      const body = new FormData(this.form);
      // Ask for the header's cart link back with the add, so BAG (NN) updates
      // in the same request (Shopify's Section Rendering API).
      const drawer = document.querySelector('cart-drawer');
      body.append('sections', drawer ? 'cart-drawer,cart-icon-bubble' : 'cart-icon-bubble');
      body.append('sections_url', window.location.pathname);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.addBtn.disabled = true;
      fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body })
        .then((r) => r.json())
        .then((res) => {
          // Sold out / not enough stock comes back as JSON with a status.
          if (res.status && res.status >= 400) {
            const msg = res.description || res.message || '';
            if (this.addText) this.addText.textContent = msg.length && msg.length < 40 ? msg : this.labels.unavailable;
            setTimeout(() => {
              this.addBtn.disabled = false;
              if (this.addText) this.addText.textContent = this.defaultAdd;
            }, 2400);
            return;
          }
          this.addBtn.classList.add('is-added');
          if (this.addText) this.addText.textContent = this.labels.added;
          if (drawer && res.sections && res.sections['cart-drawer']) {
            drawer.classList.remove('is-empty');
            drawer.setActiveElement(this.addBtn);
            drawer.renderContents(res);
          } else if (res.sections && res.sections['cart-icon-bubble']) {
            this.paintCart(res.sections['cart-icon-bubble']);
          } else this.refreshCartCount();
          // Dawn's own cart pieces listen for this (PUB_SUB_EVENTS is a global const).
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: drawer ? 'cart-drawer-items' : 'atletica-pdp', productVariantId: res.variant_id, cartData: res });
          }
          setTimeout(() => {
            this.addBtn.classList.remove('is-added');
            this.addBtn.disabled = false;
            if (this.addText) this.addText.textContent = this.defaultAdd;
          }, 1800);
        })
        .catch(() => {
          this.addBtn.disabled = false;
          if (this.addText) this.addText.textContent = this.defaultAdd;
        });
    });
  }

  // The header's cart link, re-rendered by Shopify (sections/cart-icon-bubble).
  paintCart(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const inner = doc.querySelector('.shopify-section');
    if (!inner) return;
    bubble.innerHTML = inner.innerHTML;
    bubble.classList.toggle('header__icon--cart-full', !!bubble.querySelector('.header__cart-count--full'));
  }

  refreshCartCount() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + '?sections=cart-icon-bubble', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((sections) => this.paintCart(sections['cart-icon-bubble']))
      .catch(() => {});
  }

  // ---- zoom lightbox (via the + button OR clicking the image) ----
  wireZoom() {
    const open = () => {
      const active = this.imgs[this.active];
      if (!active || active.tagName !== 'IMG' || !active.src) return;
      const ov = document.createElement('div');
      ov.className = 'atletica-pdp__lightbox';
      const img = document.createElement('img');
      img.src = active.src;
      ov.appendChild(img);
      ov.addEventListener('click', () => ov.remove());
      document.addEventListener('keydown', function esc(e) {
        if (e.key === 'Escape') { ov.remove(); document.removeEventListener('keydown', esc); }
      });
      document.body.appendChild(ov);
    };
    const btn = this.root.querySelector('[data-zoom]');
    if (btn) btn.addEventListener('click', open);
    this.imgs.forEach((im) => {
      if (im.tagName !== 'IMG') return;
      im.style.cursor = 'zoom-in';
      im.addEventListener('click', open);
    });
  }
}

function initAtleticaPDP() {
  document.querySelectorAll('[data-atletica-pdp]').forEach((root) => {
    if (!root.__atleticaPDP) root.__atleticaPDP = new AtleticaPDP(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAtleticaPDP);
} else {
  initAtleticaPDP();
}
document.addEventListener('shopify:section:load', initAtleticaPDP);
